/**
 * Deriv Options REST: one-time WebSocket URL for OAuth2 (V2) sessions.
 * Matches PU `src/external/bot-skeleton/services/api/options-rest.ts` — Bearer must not be sent as
 * legacy `authorize` on `websockets/v3` (InputValidationFailed); connect using returned URL instead.
 *
 * URLs are stable production endpoints (same as New-docs Options Setup); intentionally not driven by env —
 * OTP response provides the ephemeral `wss://…` circuit URL.
 */

import { getOAuthDerivAppIdFromEverymanSync } from './config/config';

/** Production Options REST API host (staging would be explicit if Deriv documents it separately). */
const DERIV_OPTIONS_REST_ORIGIN = 'https://api.derivws.com';
const DERIV_OPTIONS_PATH_PREFIX = '/trading/v1/options';

/** All V2 REST calls are bounded — app boot must never hang on a stalled network. */
const OPTIONS_REST_TIMEOUT_MS = 8000;

export type TOptionsOtpWsUrlResponse = {
    url: string;
};

/**
 * V2 Options account (`GET /trading/v1/options/accounts`). `account_type` replaces the legacy
 * `is_virtual` flag; there is no multi-account WS any more (one OTP connection per account).
 */
export type TOptionsAccount = {
    account_id: string;
    balance: number;
    currency: string;
    group: string;
    status: string;
    account_type: 'demo' | 'real';
};

export async function requestOptionsOtpWsUrl(
    accountId: string,
    accessToken?: string
): Promise<TOptionsOtpWsUrlResponse> {
    if (!accountId?.trim()) {
        throw new Error('requestOptionsOtpWsUrl: accountId is required');
    }
    const token = accessToken ?? readStoredAccessToken();
    if (!token) {
        throw new Error('requestOptionsOtpWsUrl: missing OAuth2 access token');
    }

    const appIdStr = resolveDerivAppIdHeaderValue();
    if (!appIdStr) {
        throw new Error(
            'requestOptionsOtpWsUrl: missing Deriv-App-ID — pass ?client_id=… from the parent (or set OAUTH_CLIENT_ID)'
        );
    }

    const url = `${DERIV_OPTIONS_REST_ORIGIN}${DERIV_OPTIONS_PATH_PREFIX}/accounts/${encodeURIComponent(accountId.trim())}/otp`;
    const response = await fetchWithTimeout(url, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Deriv-App-ID': appIdStr,
            Accept: 'application/json',
        },
    });

    const text = await response.text();
    let json: unknown = null;
    if (text) {
        try {
            json = JSON.parse(text);
        } catch {
            json = null;
        }
    }

    if (!response.ok) {
        const body = json as { errors?: Array<{ message?: string }> } | null;
        /* Live V2 401s are plain text ("Invalid or expired token"); validation errors are JSON `errors[]`. */
        const msg = body?.errors?.[0]?.message || text.trim() || `HTTP ${response.status}`;
        throw new Error(msg);
    }

    if (!json) {
        throw new Error(`requestOptionsOtpWsUrl: non-JSON response (${response.status})`);
    }

    const body = json as { data?: { url?: string } };
    if (!body?.data?.url) {
        throw new Error('requestOptionsOtpWsUrl: response missing data.url');
    }
    return { url: body.data.url };
}

/** Stored OAuth2 Bearer token (localStorage) — same key the bridge/root write. */
const readStoredAccessToken = (): string => {
    try {
        return typeof localStorage !== 'undefined' ? localStorage.getItem('oauth2_access_token') || '' : '';
    } catch {
        return '';
    }
};

/**
 * `Deriv-App-ID` header value for V2 Options REST/OTP. On V2 this is the alphanumeric OAuth2 client id
 * (never the legacy numeric app id). Resolution lives in `@deriv/shared` config:
 * parent sync keys → build/env `OAUTH_CLIENT_ID`.
 */
const resolveDerivAppIdHeaderValue = (): string =>
    String(getOAuthDerivAppIdFromEverymanSync() ?? '').trim();

/** fetch() bounded by `OPTIONS_REST_TIMEOUT_MS` so boot/OTP can't hang indefinitely. */
const fetchWithTimeout = async (url: string, init: RequestInit): Promise<Response> => {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), OPTIONS_REST_TIMEOUT_MS) : null;
    try {
        return await fetch(url, controller ? { ...init, signal: controller.signal } : init);
    } finally {
        if (timer) clearTimeout(timer);
    }
};

/**
 * `GET /trading/v1/options/accounts` — every Options account on this OAuth2 session (demo + real).
 * Requires scope `trade`. The response may hold a single account object or an array; both normalised.
 */
export async function getOptionsAccounts(accessToken?: string): Promise<TOptionsAccount[]> {
    const token = accessToken ?? readStoredAccessToken();
    if (!token) {
        throw new Error('getOptionsAccounts: missing OAuth2 access token');
    }
    const appIdStr = resolveDerivAppIdHeaderValue();
    if (!appIdStr) {
        throw new Error('getOptionsAccounts: missing Deriv-App-ID — pass ?client_id=… from the parent');
    }

    const url = `${DERIV_OPTIONS_REST_ORIGIN}${DERIV_OPTIONS_PATH_PREFIX}/accounts`;
    const response = await fetchWithTimeout(url, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${token}`,
            'Deriv-App-ID': appIdStr,
            Accept: 'application/json',
        },
    });

    const text = await response.text();
    let json: unknown = null;
    if (text) {
        try {
            json = JSON.parse(text);
        } catch {
            json = null;
        }
    }

    if (!response.ok) {
        const body = json as { errors?: Array<{ message?: string }> } | null;
        /* Live V2 401s are plain text ("Invalid or expired token"); validation errors are JSON `errors[]`. */
        const msg = body?.errors?.[0]?.message || text.trim() || `HTTP ${response.status}`;
        throw new Error(msg);
    }

    if (!json) {
        throw new Error(`getOptionsAccounts: non-JSON response (${response.status})`);
    }

    const body = json as { data?: TOptionsAccount | TOptionsAccount[] };
    if (!body?.data) {
        throw new Error('getOptionsAccounts: response missing data');
    }
    return Array.isArray(body.data) ? body.data : [body.data];
}
